import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import stationApi from '../../../../utils/api/location-management';

interface ChartDataProperties {
  name: string;
  value: number;
  color: string;
}

interface ConnectorStatsState {
  total: number;
  available: number;
  preparing: number;
  charging: number;
  finishing: number;
  faulted: number;
  suspendedEv: number;
  suspendedEvse: number;
  reservations: number;
  unavailable: number;
  unknown: number;
}

/**
 * Colors for connector status chart
 * @param {string} status The status string
 * @returns {string} The color string
 */
function getConnectorStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'available':
      return '#2AA700';
    case 'preparing':
      return '#FFB800';
    case 'charging':
      return '#4ABDFB';
    case 'finishing':
      return '#00A79D';
    case 'faulted':
      return '#E41010';
    case 'suspendedev':
      return '#E91E63';
    case 'suspendedevse':
      return '#FF7A00';
    case 'reserved':
      return '#13C2C2';
    case 'unknown':
      return '#333333';
    case 'unavailable':
      return '#6C757D';
    default:
      return '#CCCCCC';
  }
}

/**
 * Connector status distribution chart component
 * @returns {any} The rendered component
 */
function ConnectorStatusDistributionChart() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const { selectedLocationPerformanceList, selectedChargingPoint } = useAppSelector(
    (state) => state.locationManagement,
  );

  const [connectorStats, setConnectorStats] = useState<ConnectorStatsState>({
    total: 0,
    available: 0,
    preparing: 0,
    charging: 0,
    finishing: 0,
    faulted: 0,
    suspendedEv: 0,
    suspendedEvse: 0,
    reservations: 0,
    unavailable: 0,
    unknown: 0,
  });

  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Function for fetching connector status count report
   */
  const fetchConnectorStatusData = async () => {
    const stationId = selectedLocationPerformanceList?.id || selectedChargingPoint?.id;
    if (!stationId) return;

    setLoading(true);
    try {
      const connectorResponse = await stationApi.getDeviceConnectorStatus({
        chargingStation: stationId,
      });

      if (connectorResponse?.status === 200 || connectorResponse?.data) {
        const connectorData = connectorResponse.data || connectorResponse;
        const counts = connectorData.statusCounts || [];

        const mappedStats: ConnectorStatsState = {
          total: connectorData.total ?? 0,
          available: 0,
          preparing: 0,
          charging: 0,
          finishing: 0,
          faulted: 0,
          suspendedEv: 0,
          suspendedEvse: 0,
          reservations: 0,
          unavailable: 0,
          unknown: 0,
        };

        counts.forEach((item: { status: string; count: number }) => {
          const status = item.status?.toLowerCase();
          const count = item.count ?? 0;
          switch (status) {
            case 'available':
              mappedStats.available = count;
              break;
            case 'preparing':
              mappedStats.preparing = count;
              break;
            case 'charging':
              mappedStats.charging = count;
              break;
            case 'finishing':
              mappedStats.finishing = count;
              break;
            case 'faulted':
              mappedStats.faulted = count;
              break;
            case 'suspendedev':
              mappedStats.suspendedEv = count;
              break;
            case 'suspendedevse':
              mappedStats.suspendedEvse = count;
              break;
            case 'reserved':
              mappedStats.reservations = count;
              break;
            case 'unavailable':
              mappedStats.unavailable = count;
              break;
            case 'unknown':
              mappedStats.unknown = count;
              break;
            default:
              break;
          }
        });

        setConnectorStats(mappedStats);

        const statusItems = [
          {
            label: 'Available',
            value: mappedStats.available,
            color: getConnectorStatusColor('Available'),
          },
          {
            label: 'Preparing',
            value: mappedStats.preparing,
            color: getConnectorStatusColor('Preparing'),
          },
          {
            label: 'Charging',
            value: mappedStats.charging,
            color: getConnectorStatusColor('Charging'),
          },
          {
            label: 'Finishing',
            value: mappedStats.finishing,
            color: getConnectorStatusColor('Finishing'),
          },
          {
            label: 'Faulted',
            value: mappedStats.faulted,
            color: getConnectorStatusColor('Faulted'),
          },
          {
            label: 'SuspendedEV',
            value: mappedStats.suspendedEv,
            color: getConnectorStatusColor('SuspendedEV'),
          },
          {
            label: 'SuspendedEVSE',
            value: mappedStats.suspendedEvse,
            color: getConnectorStatusColor('SuspendedEVSE'),
          },
          {
            label: 'Reserved',
            value: mappedStats.reservations,
            color: getConnectorStatusColor('Reserved'),
          },
          {
            label: 'Unknown',
            value: mappedStats.unknown,
            color: getConnectorStatusColor('Unknown'),
          },
          {
            label: 'Unavailable',
            value: mappedStats.unavailable,
            color: getConnectorStatusColor('Unavailable'),
          },
        ];

        const chartData: ChartDataProperties[] = statusItems.map((item) => ({
          name: item.label,
          value: item.value,
          color: item.color,
        }));

        setBarGraphData(chartData);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Function for tooltip
   * @param {any} props The tooltip props
   * @param {boolean} props.active Whether the tooltip is active
   * @param {any[]} props.payload The tooltip payload
   * @returns {any} The rendered tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div
          className="connector-status-custom-tooltip"
          style={{ '--status-color': data?.color || data?.payload?.color } as React.CSSProperties}>
          <p className="label">
            {`${t(data.name.toLowerCase(), data.name)} : ${data.value}`}
          </p>
        </div>
      );
    }
    return null;
  }

  useEffect(() => {
    fetchConnectorStatusData();
  }, [selectedLocationPerformanceList?.id, selectedChargingPoint?.id]);

  const activeChartData = barGraphData.filter((item) => item.value > 0);

  return (
    <div className="location-status-distributionChart-wrap-report" dir={i18n.dir()}>
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <span className="title-text">
              {t('connectorStatusOverview', 'Connector Status Overview')}
            </span>
            <p className="subtitle-text">
              {t(
                'connectorStatusOverviewDesc',
                'Overview of all connector statuses in the system',
              )}
            </p>
          </div>
        </div>
        <div className="energy-report-contain">
          {loading ? (
            <center className="loader-button">
              <BatteryLoader />
            </center>
          ) : (
            <>
              <div className="piechart-container">
                <PieChart
                  width={250}
                  height={250}
                  margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <Pie
                    data={activeChartData.length > 0 ? activeChartData : barGraphData}
                    cx={125}
                    cy={125}
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    isAnimationActive={false}>
                    {(activeChartData.length > 0 ? activeChartData : barGraphData).map((entry) => (
                      <Cell key={entry.name} fill={String(entry.color)} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>

                <div className="total-sessions-wrap">
                  <p>{t('total', 'Total')}</p>
                  <span>{connectorStats.total}</span>
                </div>
              </div>

              <div className="PieChart-heading">
                <div className="pie-chart-label">
                  {[
                    {
                      label: 'Available',
                      value: connectorStats.available,
                      color: getConnectorStatusColor('Available'),
                    },
                    {
                      label: 'Preparing',
                      value: connectorStats.preparing,
                      color: getConnectorStatusColor('Preparing'),
                    },
                    {
                      label: 'Charging',
                      value: connectorStats.charging,
                      color: getConnectorStatusColor('Charging'),
                    },
                    {
                      label: 'Finishing',
                      value: connectorStats.finishing,
                      color: getConnectorStatusColor('Finishing'),
                    },
                    {
                      label: 'Faulted',
                      value: connectorStats.faulted,
                      color: getConnectorStatusColor('Faulted'),
                    },
                    {
                      label: 'SuspendedEV',
                      value: connectorStats.suspendedEv,
                      color: getConnectorStatusColor('SuspendedEV'),
                    },
                    {
                      label: 'SuspendedEVSE',
                      value: connectorStats.suspendedEvse,
                      color: getConnectorStatusColor('SuspendedEVSE'),
                    },
                    {
                      label: 'Reserved',
                      value: connectorStats.reservations,
                      color: getConnectorStatusColor('Reserved'),
                    },
                    {
                      label: 'Unknown',
                      value: connectorStats.unknown,
                      color: getConnectorStatusColor('Unknown'),
                    },
                    {
                      label: 'Unavailable',
                      value: connectorStats.unavailable,
                      color: getConnectorStatusColor('Unavailable'),
                    },
                  ].map((item) => (
                    <div key={item.label} className="each-label">
                      <div
                        className="each-label-color"
                        style={{ '--label-color': item.color } as React.CSSProperties}
                      />
                      <p>{t(item.label.toLowerCase(), item.label)}</p>
                      <div className="each-label-value">
                        <span>{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConnectorStatusDistributionChart;
